import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlertTriangle, ArrowLeft, Archive, CheckCircle2, Clock3, Pencil } from "lucide-react";
import { PageContainer } from "@/layout/PageContainer";
import { Badge } from "@/ui/Badge";
import { Button } from "@/ui/Button";
import { Card, CardBody, SectionHeading } from "@/ui/Card";
import { DataTable, type Column } from "@/ui/data-table/DataTable";
import { EmptyState, LoadingState } from "@/ui/feedback/states";
import { ApiFallbackBanner } from "@/ui/feedback/ApiFallbackBanner";
import { ConfirmDialog } from "@/ui/modals/ConfirmDialog";
import { StatTile } from "@/ui/StatTile";
import { toast } from "@/stores/toast.store";
import { getErrorMessage } from "@/lib/api-errors";
import { formatDZD, formatMoney } from "@/lib/formatters";
import { useLabels } from "@/lib/labels";
import { useClients } from "@/features/clients/clients.hooks";
import type { Payment, PaymentStatus } from "@/types";
import type { PaymentInput } from "./payments.adapter";
import { PaymentFormModal } from "./PaymentFormModal";
import { useArchivePayment, usePayment, usePayments, useUpdatePayment, useUpdatePaymentStatus } from "./payments.hooks";
import { buildPaymentStatusUpdate, getPaymentNotePeriod, summarizeClientPayments } from "./payment-details.logic";

export function PaymentDetailsPage() {
  const { paymentId = "" } = useParams<{ paymentId: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { PAYMENT_STATUS, PAYMENT_TYPE, PAYMENT_METHOD } = useLabels();
  const { payment, isLoading, isError, source } = usePayment(paymentId);
  const { payments } = usePayments();
  const { clients } = useClients();
  const updateStatus = useUpdatePaymentStatus();
  const updatePayment = useUpdatePayment();
  const archivePayment = useArchivePayment();
  const [editOpen, setEditOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [statusAction, setStatusAction] = useState<PaymentStatus | null>(null);

  const clientSummary = useMemo(() => (payment ? summarizeClientPayments(payment, payments) : null), [payment, payments]);

  if (isLoading) {
    return (
      <PageContainer>
        <LoadingState rows={6} />
      </PageContainer>
    );
  }

  if (!payment) {
    return (
      <PageContainer>
        <EmptyState
          title={t("payments.details.notFound")}
          action={
            <Button size="sm" onClick={() => navigate("/payments")}>
              {t("payments.details.back")}
            </Button>
          }
        />
      </PageContainer>
    );
  }

  const methodLabel = payment.method ? PAYMENT_METHOD[payment.method].label : t("payments.emptyValue");
  const dateLabel = payment.receivedAt
    ? `${t("payments.details.receivedDate")}: ${payment.receivedAt}`
    : payment.dueDate
      ? `${t("payments.details.dueDate")}: ${payment.dueDate}`
      : t("payments.emptyValue");

  const historyColumns: Column<Payment>[] = [
    { key: "type", header: t("payments.columns.type"), cell: (item) => PAYMENT_TYPE[item.type].label },
    {
      key: "note",
      header: t("payments.columns.notePeriod"),
      cell: (item) => {
        const notePeriod = getPaymentNotePeriod(item);
        return (
          <div>
            <div className="text-text2">{notePeriod.primary}</div>
            {notePeriod.secondary && <div className="text-[11px] text-text3">{notePeriod.secondary}</div>}
          </div>
        );
      },
    },
    { key: "amount", header: t("payments.columns.amount"), align: "right", cell: (item) => <span className="mono">{formatDZD(item.amount)}</span> },
    {
      key: "status",
      header: t("payments.columns.status"),
      cell: (item) => (
        <Badge tone={PAYMENT_STATUS[item.status].tone} dot>
          {PAYMENT_STATUS[item.status].label}
        </Badge>
      ),
    },
    { key: "date", header: t("payments.columns.date"), align: "right", cell: (item) => <span className="text-text3">{item.date}</span> },
  ];

  const detailRows: Array<[string, string]> = [
    [t("payments.details.client"), payment.clientName],
    [t("payments.details.type"), PAYMENT_TYPE[payment.type].label],
    [t("payments.details.method"), methodLabel],
    [t("payments.details.status"), PAYMENT_STATUS[payment.status].label],
    [t("payments.details.amount"), formatMoney(payment.amount, payment.currency)],
    [t("payments.details.currency"), payment.currency],
    [t("payments.details.period"), payment.period ?? t("payments.emptyValue")],
    [t("payments.details.note"), payment.note ?? t("payments.emptyValue")],
    [t("payments.details.dueDate"), payment.dueDate ?? t("payments.emptyValue")],
    [t("payments.details.receivedDate"), payment.receivedAt ?? t("payments.emptyValue")],
    [t("payments.details.createdDate"), payment.createdAt ?? t("payments.emptyValue")],
    [t("payments.details.updatedDate"), payment.updatedAt ?? t("payments.emptyValue")],
  ];

  const onStatus = async (status: PaymentStatus) => {
    setStatusAction(status);
    try {
      await updateStatus.mutateAsync({ id: payment.id, ...buildPaymentStatusUpdate(status) });
      toast.success(t("payments.statusUpdated"));
    } catch (err) {
      toast.error(getErrorMessage(err, t("payments.updateStatusFailed")));
    } finally {
      setStatusAction(null);
    }
  };

  const onEdit = async (input: PaymentInput) => {
    try {
      await updatePayment.mutateAsync({ id: payment.id, input });
      toast.success(t("payments.updated"));
      setEditOpen(false);
    } catch (err) {
      toast.error(getErrorMessage(err, t("payments.updateFailed")));
    }
  };

  const onArchive = async () => {
    try {
      await archivePayment.mutateAsync(payment.id);
      toast.success(t("payments.archived"));
      navigate("/payments");
    } catch (err) {
      toast.error(getErrorMessage(err, t("payments.archiveFailed")));
    } finally {
      setArchiveOpen(false);
    }
  };

  return (
    <PageContainer>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate("/payments")}>
          <ArrowLeft className="h-3.5 w-3.5" /> {t("payments.details.back")}
        </Button>
        {isError && source === "mock" && (
          <div className="min-w-[260px] flex-1">
            <ApiFallbackBanner message={t("dashboard.banner.apiDown")} actionLabel={t("states.retry")} />
          </div>
        )}
      </div>

      <Card className="mb-5">
        <CardBody className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div className="min-w-0">
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <h1 className="font-display text-[24px] font-bold text-text">{payment.clientName}</h1>
              <Badge tone={PAYMENT_STATUS[payment.status].tone} dot>
                {PAYMENT_STATUS[payment.status].label}
              </Badge>
            </div>
            <div className="text-sm font-semibold text-text2">{PAYMENT_TYPE[payment.type].label}</div>
            <div className="mt-2 text-[12px] text-text3">{payment.period ?? payment.note ?? t("payments.emptyValue")}</div>
          </div>
          <div className="mono text-[28px] font-bold text-text">{formatMoney(payment.amount, payment.currency)}</div>
        </CardBody>
      </Card>

      <div className="mb-5 grid grid-cols-1 gap-3 md:grid-cols-4">
        <StatTile label={t("payments.details.amount")} value={formatMoney(payment.amount, payment.currency)} tone="primary" />
        <StatTile label={t("payments.details.status")} value={PAYMENT_STATUS[payment.status].label} tone={PAYMENT_STATUS[payment.status].tone} />
        <StatTile label={t("payments.details.method")} value={methodLabel} />
        <StatTile label={payment.receivedAt ? t("payments.details.receivedDate") : t("payments.details.dueDate")} value={dateLabel} />
      </div>

      <div className="mb-5 grid grid-cols-1 gap-4 xl:grid-cols-[1.2fr_0.8fr]">
        <Card>
          <CardBody>
            <SectionHeading title={t("payments.details.paymentSummary")} />
            <div className="divide-y divide-border">
              {detailRows.map(([label, value]) => (
                <DetailRow key={label} label={label} value={value} />
              ))}
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <SectionHeading title={t("payments.details.actions")} />
            <div className="grid gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="justify-start text-success"
                disabled={payment.status === "RECEIVED"}
                loading={statusAction === "RECEIVED"}
                onClick={() => onStatus("RECEIVED")}
              >
                <CheckCircle2 className="h-3.5 w-3.5" /> {t("payments.details.markAsReceived")}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="justify-start text-warning"
                disabled={payment.status === "PENDING"}
                loading={statusAction === "PENDING"}
                onClick={() => onStatus("PENDING")}
              >
                <Clock3 className="h-3.5 w-3.5" /> {t("payments.details.markAsPending")}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="justify-start text-danger"
                disabled={payment.status === "OVERDUE"}
                loading={statusAction === "OVERDUE"}
                onClick={() => onStatus("OVERDUE")}
              >
                <AlertTriangle className="h-3.5 w-3.5" /> {t("payments.details.markAsOverdue")}
              </Button>
              <Button variant="ghost" size="sm" className="justify-start" onClick={() => setEditOpen(true)}>
                <Pencil className="h-3.5 w-3.5" /> {t("payments.details.edit")}
              </Button>
              <Button variant="danger" size="sm" className="justify-start" onClick={() => setArchiveOpen(true)}>
                <Archive className="h-3.5 w-3.5" /> {t("common.archive")}
              </Button>
            </div>
          </CardBody>
        </Card>
      </div>

      <Card>
        <CardBody>
          <SectionHeading title={t("payments.details.clientPaymentHistory")} subtitle={t("payments.details.clientTotals")} />
          {clientSummary && (
            <>
              <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-4">
                <StatTile label={t("payments.details.totalReceived")} value={<span className="text-success">{formatDZD(clientSummary.totalReceived)}</span>} tone="success" />
                <StatTile label={t("payments.details.totalPending")} value={<span className="text-warning">{formatDZD(clientSummary.totalPending)}</span>} tone="warning" />
                <StatTile label={t("payments.details.totalOverdue")} value={<span className="text-danger">{formatDZD(clientSummary.totalOverdue)}</span>} tone="danger" />
                <StatTile label={t("payments.details.totalRegistered")} value={String(clientSummary.count)} />
              </div>
              <DataTable
                columns={historyColumns}
                rows={clientSummary.payments}
                rowKey={(item) => item.id}
                empty={{ title: t("payments.details.noHistory") }}
              />
            </>
          )}
        </CardBody>
      </Card>

      <PaymentFormModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        onSave={onEdit}
        clients={clients}
        initial={payment}
        saving={updatePayment.isPending}
      />
      <ConfirmDialog
        open={archiveOpen}
        onClose={() => setArchiveOpen(false)}
        title={t("payments.archiveTitle")}
        message={t("payments.archiveMessage", { client: payment.clientName, amount: formatDZD(payment.amount) })}
        confirmLabel={t("common.archive")}
        onConfirm={onArchive}
      />
    </PageContainer>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2.5">
      <span className="text-xs font-medium text-text2">{label}</span>
      <span className="max-w-[65%] text-right text-[12.5px] font-semibold text-text whitespace-pre-wrap">{value}</span>
    </div>
  );
}
